import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ContactAttributeDefinitionApiService } from '../services/contact-attribute-definition-api.service';
import { ContactAttributeDefinitionRequest } from '../models/contact-attribute-definition.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';
import { NAME_PATTERN } from '../../../shared/utils/text-normalization.utils';

export interface TypeOption {
  value: 'text' | 'number' | 'boolean' | 'list';
  label: string;
  description: string;
}

@Component({
  selector: 'app-contact-attribute-definition-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './contact-attribute-definition-form.component.html'
})
export class ContactAttributeDefinitionFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  definitionId: number | null = null;

  // Form model
  name = '';
  private originalName = '';
  type: 'text' | 'number' | 'boolean' | 'list' = 'text';

  // List options builder
  listOptionsList: string[] = [];
  newOptionInput = '';
  duplicateError = false;

  readonly typeOptions: TypeOption[] = [
    { value: 'text',    label: 'Texto',    description: 'Valor libre · búsqueda parcial' },
    { value: 'number',  label: 'Número',   description: 'Valor numérico · búsqueda exacta' },
    { value: 'boolean', label: 'Sí / No',  description: 'Casilla activado/desactivado' },
    { value: 'list',    label: 'Lista',    description: 'Opciones predefinidas a elegir' },
  ];

  constructor(
    private definitionApi: ContactAttributeDefinitionApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.definitionId = +id;
      this.cargarDefinicion(this.definitionId);
    }
  }

  private cargarDefinicion(id: number): void {
    this.loading = true;
    this.error = '';

    this.definitionApi.getDefinitionById(id).subscribe({
      next: (def) => {
        this.name = def.name;
        this.originalName = def.name;
        this.type = def.type as 'text' | 'number' | 'boolean' | 'list';
        if (def.listOptions) {
          try { this.listOptionsList = JSON.parse(def.listOptions); } catch { this.listOptionsList = []; }
        }
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la definición';
        this.loading = false;
      }
    });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Definición' : 'Nueva Definición';
  }

  selectType(value: 'text' | 'number' | 'boolean' | 'list'): void {
    this.type = value;
    if (value !== 'list') {
      this.newOptionInput = '';
      this.duplicateError = false;
    }
  }

  addOption(): void {
    const val = this.newOptionInput.trim();
    if (!val) return;
    if (this.listOptionsList.includes(val)) {
      this.duplicateError = true;
      return;
    }
    this.duplicateError = false;
    this.listOptionsList = [...this.listOptionsList, val];
    this.newOptionInput = '';
  }

  removeOption(index: number): void {
    this.listOptionsList = this.listOptionsList.filter((_, i) => i !== index);
  }

  moveOption(index: number, direction: -1 | 1): void {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= this.listOptionsList.length) return;
    const arr = [...this.listOptionsList];
    [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
    this.listOptionsList = arr;
  }

  guardar(): void {
    this.error = '';
    if (!this.name.trim()) {
      this.error = 'El nombre es requerido';
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }
    if (!NAME_PATTERN.test(this.name.trim())) {
      const msg = 'El nombre solo puede contener letras, espacios, apóstrofes y guiones';
      this.error = msg;
      this.toast.warning({ title: 'Nombre inválido', message: msg });
      return;
    }
    if (this.type === 'list' && this.listOptionsList.length === 0) {
      this.error = 'Agrega al menos una opción para el tipo Lista';
      this.toast.warning({ title: 'Faltan opciones', message: this.error });
      return;
    }

    this.saving = true;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const request: ContactAttributeDefinitionRequest = {
      businessId,
      name: this.name.trim(),
      type: this.type,
      listOptions: this.type === 'list' ? JSON.stringify(this.listOptionsList) : undefined
    };

    if (this.isEditing && this.definitionId) {
      this.definitionApi.updateDefinition(this.definitionId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Definición de atributo',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-contactos/definiciones']);
        },
        error: (err) => {
          this.saving = false;
          if (err.status === 409) {
            const msg = `Ya existe una definición con el nombre "${this.name.trim()}"`;
            this.error = msg;
            this.toast.warning({ title: 'Nombre duplicado', message: msg });
          } else {
            this.error = err.error?.message || 'Error al actualizar la definición';
            this.toast.error({ title: 'Error', message: this.error });
          }
        }
      });
    } else {
      this.definitionApi.createDefinition(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Definición de atributo', `Se creó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-contactos/definiciones']);
        },
        error: (err) => {
          this.saving = false;
          if (err.status === 409) {
            const msg = `Ya existe una definición con el nombre "${this.name.trim()}"`;
            this.error = msg;
            this.toast.warning({ title: 'Nombre duplicado', message: msg });
          } else {
            this.error = err.error?.message || 'Error al crear la definición';
            this.toast.error({ title: 'Error', message: this.error });
          }
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-contactos/definiciones']);
  }
}
